// Class-v2 Mock Data - New Class View with band grouping and option blocks
// Supports: Year 11, 10 (8 bands, option blocks), Year 9, 8, 7 (6 bands, no option blocks)

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Week in 2-week cycle */
export type Week = 'A' | 'B';

/** Days of the week */
export type Day = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';

/** Period numbers (1-5) */
export type Period = 1 | 2 | 3 | 4 | 5;

/** Year group identifiers in display order (11 first, 7 last) */
export type YearGroupId = 11 | 10 | 9 | 8 | 7;

/** Subject with fixed color */
export interface Subject {
  code: string;
  name: string;
  color: string;
}

/** Set within a cell (H, O, L, I, C, N, S) */
export interface SetInfo {
  code: string;
  subjectCode: string;
}

/** Subject within an Option Block */
export interface OptionBlockSubject {
  subject: Subject;
  sets?: SetInfo[];
}

/** Option Block (A, B, or C) - only for Year 11/10 */
export interface OptionBlock {
  id: 'A' | 'B' | 'C';
  subjects: OptionBlockSubject[];
}

/** Option block placement for a specific period */
export interface OptionBlockPlacement {
  period: Period;
  blocks: OptionBlock[];
}

/** Regular timetable cell */
export interface TimetableCell {
  id: string;
  bandSpan: string[];
  periodSpan: Period[];
  subject: Subject;
  sets?: SetInfo[];
  isOptional?: boolean;
}

/** Band configuration per year group */
export interface YearGroupConfig {
  id: YearGroupId;
  bands: string[];
  hasOptionBlocks: boolean;
  canHaveMultiPeriod: boolean;
}

/** Year group data for a specific day */
export interface YearGroupDayData {
  yearGroupId: YearGroupId;
  cells: TimetableCell[];
  optionBlocks?: OptionBlockPlacement[];
}

/** Complete day data */
export interface DayData {
  week: Week;
  day: Day;
  yearGroups: YearGroupDayData[];
}

/** Root data structure for Class-v2 */
export interface ClassV2Data {
  yearGroupConfigs: YearGroupConfig[];
  weeks: Week[];
  days: Day[];
  periodsPerDay: number;
  dayData: DayData[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Subject colors - each subject has a unique fixed color */
export const subjectColors: Record<string, Subject> = {
  // Mathematics - Blue shades
  Mat: { code: 'Mat', name: 'Mathematics', color: '#3B82F6' },
  MatI: { code: 'MatI', name: 'Mathematics Intermediate', color: '#3B82F6' },
  MatC: { code: 'MatC', name: 'Mathematics Core', color: '#3B82F6' },
  MatN: { code: 'MatN', name: 'Mathematics Numeracy', color: '#3B82F6' },
  MatS: { code: 'MatS', name: 'Mathematics Statistics', color: '#3B82F6' },
  MatL: { code: 'MatL', name: 'Mathematics Lower', color: '#3B82F6' },
  MatH: { code: 'MatH', name: 'Mathematics Higher', color: '#60A5FA' },
  Maths: { code: 'Maths', name: 'Mathematics', color: '#3B82F6' },

  // History - Yellow
  His: { code: 'His', name: 'History', color: '#FCD34D' },
  HisH: { code: 'HisH', name: 'History Higher', color: '#FCD34D' },
  HisO: { code: 'HisO', name: 'History Ordinary', color: '#FCD34D' },
  HisL: { code: 'HisL', name: 'History Lower', color: '#FCD34D' },
  HisS: { code: 'HisS', name: 'History Standard', color: '#FCD34D' },

  // Art - Green
  Art: { code: 'Art', name: 'Art', color: '#86EFAC' },
  ArtH: { code: 'ArtH', name: 'Art Higher', color: '#86EFAC' },
  ArtO: { code: 'ArtO', name: 'Art Ordinary', color: '#86EFAC' },
  ArtL: { code: 'ArtL', name: 'Art Lower', color: '#86EFAC' },
  ArtI: { code: 'ArtI', name: 'Art Intermediate', color: '#86EFAC' },

  // Geography - Purple/Lavender
  Geo: { code: 'Geo', name: 'Geography', color: '#C4B5FD' },
  GeoH: { code: 'GeoH', name: 'Geography Higher', color: '#C4B5FD' },
  GeoO: { code: 'GeoO', name: 'Geography Ordinary', color: '#C4B5FD' },
  GeoL: { code: 'GeoL', name: 'Geography Lower', color: '#C4B5FD' },
  GeoI: { code: 'GeoI', name: 'Geography Intermediate', color: '#C4B5FD' },
  GeoN: { code: 'GeoN', name: 'Geography Natural', color: '#C4B5FD' },
  GeoS: { code: 'GeoS', name: 'Geography Standard', color: '#C4B5FD' },
  GeoC: { code: 'GeoC', name: 'Geography Core', color: '#C4B5FD' },
  Geol: { code: 'Geol', name: 'Geology', color: '#A78BFA' },

  // Science - Teal/Cyan
  Sci: { code: 'Sci', name: 'Science', color: '#5EEAD4' },
  SciH: { code: 'SciH', name: 'Science Higher', color: '#5EEAD4' },
  SciO: { code: 'SciO', name: 'Science Ordinary', color: '#5EEAD4' },
  SciL: { code: 'SciL', name: 'Science Lower', color: '#5EEAD4' },
  SciI: { code: 'SciI', name: 'Science Intermediate', color: '#5EEAD4' },
  SciN: { code: 'SciN', name: 'Science Natural', color: '#5EEAD4' },
  SciS: { code: 'SciS', name: 'Science Standard', color: '#5EEAD4' },

  // English - Pink/Rose
  Eng: { code: 'Eng', name: 'English', color: '#FDA4AF' },
  EngH: { code: 'EngH', name: 'English Higher', color: '#FDA4AF' },
  EngO: { code: 'EngO', name: 'English Ordinary', color: '#FDA4AF' },
  EngL: { code: 'EngL', name: 'English Lower', color: '#FDA4AF' },
  EngI: { code: 'EngI', name: 'English Intermediate', color: '#FDA4AF' },
  EngN: { code: 'EngN', name: 'English Natural', color: '#FDA4AF' },
  EngS: { code: 'EngS', name: 'English Standard', color: '#FDA4AF' },

  // PE - Orange
  PE: { code: 'PE', name: 'Physical Education', color: '#FDBA74' },
  PEH: { code: 'PEH', name: 'PE Higher', color: '#FDBA74' },
  PEO: { code: 'PEO', name: 'PE Ordinary', color: '#FDBA74' },
  PEL: { code: 'PEL', name: 'PE Lower', color: '#FDBA74' },
  PEI: { code: 'PEI', name: 'PE Intermediate', color: '#FDBA74' },
  PEN: { code: 'PEN', name: 'PE Natural', color: '#FDBA74' },
  PES: { code: 'PES', name: 'PE Standard', color: '#FDBA74' },

  // Music - Indigo
  Mus: { code: 'Mus', name: 'Music', color: '#A5B4FC' },
  MusH: { code: 'MusH', name: 'Music Higher', color: '#A5B4FC' },
  MusO: { code: 'MusO', name: 'Music Ordinary', color: '#A5B4FC' },
  MusL: { code: 'MusL', name: 'Music Lower', color: '#A5B4FC' },
  MusI: { code: 'MusI', name: 'Music Intermediate', color: '#A5B4FC' },
  MusN: { code: 'MusN', name: 'Music Natural', color: '#A5B4FC' },
  MusS: { code: 'MusS', name: 'Music Standard', color: '#A5B4FC' },

  // French - Sky Blue
  Fre: { code: 'Fre', name: 'French', color: '#7DD3FC' },
  FreH: { code: 'FreH', name: 'French Higher', color: '#7DD3FC' },
  FreO: { code: 'FreO', name: 'French Ordinary', color: '#7DD3FC' },
  FreL: { code: 'FreL', name: 'French Lower', color: '#7DD3FC' },
  FreI: { code: 'FreI', name: 'French Intermediate', color: '#7DD3FC' },
  FreN: { code: 'FreN', name: 'French Natural', color: '#7DD3FC' },
  FreS: { code: 'FreS', name: 'French Standard', color: '#7DD3FC' },

  // DT (Design Technology) - Amber
  DT: { code: 'DT', name: 'Design Technology', color: '#FDE047' },
  DTH: { code: 'DTH', name: 'DT Higher', color: '#FDE047' },
  DTO: { code: 'DTO', name: 'DT Ordinary', color: '#FDE047' },
  DTL: { code: 'DTL', name: 'DT Lower', color: '#FDE047' },
  DTI: { code: 'DTI', name: 'DT Intermediate', color: '#FDE047' },
  DTN: { code: 'DTN', name: 'DT Natural', color: '#FDE047' },
  DTS: { code: 'DTS', name: 'DT Standard', color: '#FDE047' },

  // RE (Religious Education) - Lime
  RE: { code: 'RE', name: 'Religious Education', color: '#BEF264' },
  REH: { code: 'REH', name: 'RE Higher', color: '#BEF264' },
  REO: { code: 'REO', name: 'RE Ordinary', color: '#BEF264' },
  REL: { code: 'REL', name: 'RE Lower', color: '#BEF264' },
  REI: { code: 'REI', name: 'RE Intermediate', color: '#BEF264' },
  REN: { code: 'REN', name: 'RE Natural', color: '#BEF264' },
  RES: { code: 'RES', name: 'RE Standard', color: '#BEF264' },
};

/** Year group configurations */
export const yearGroupConfigs: YearGroupConfig[] = [
  {
    id: 11,
    bands: ['11A', '11B', '11C', '11D', '11E', '11F', '11G', '11H'],
    hasOptionBlocks: true,
    canHaveMultiPeriod: true,
  },
  {
    id: 10,
    bands: ['10A', '10B', '10C', '10D', '10E', '10F', '10G', '10H'],
    hasOptionBlocks: true,
    canHaveMultiPeriod: true,
  },
  {
    id: 9,
    bands: ['9H', '9I', '9L', '9N', '9O', '9S'],
    hasOptionBlocks: false,
    canHaveMultiPeriod: false,
  },
  {
    id: 8,
    bands: ['8H', '8I', '8L', '8N', '8O', '8S'],
    hasOptionBlocks: false,
    canHaveMultiPeriod: false,
  },
  {
    id: 7,
    bands: ['7H', '7I', '7L', '7N', '7O', '7S'],
    hasOptionBlocks: false,
    canHaveMultiPeriod: false,
  },
];

/** All days in order */
export const allDays: Day[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/** All weeks */
export const allWeeks: Week[] = ['A', 'B'];

/** Periods per day */
export const periodsPerDay = 5;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getSubject = (code: string): Subject => {
  return subjectColors[code] || { code, name: code, color: '#94A3B8' };
};

// ============================================================================
// YEAR 11 DATA GENERATION (Based on wireframe - each cell has unique position)
// ============================================================================

const generateYear11Data = (week: Week, day: Day): YearGroupDayData => {
  const cells: TimetableCell[] = [];

  // Period 1: Each band group has ONE cell (no overlapping)
  // 11A-11B: His with sets
  cells.push({
    id: `y11-${week}-${day}-p1-his-ab`,
    bandSpan: ['11A', '11B'],
    periodSpan: [1],
    subject: getSubject('His'),
    sets: [
      { code: 'H', subjectCode: 'HisH' },
      { code: 'O', subjectCode: 'HisO' },
    ],
  });

  // 11C-11D: Mat with sets (single cell covering both bands)
  cells.push({
    id: `y11-${week}-${day}-p1-mat-cd`,
    bandSpan: ['11C', '11D'],
    periodSpan: [1],
    subject: getSubject('Mat'),
    sets: [
      { code: 'I', subjectCode: 'MatI' },
      { code: 'C', subjectCode: 'MatC' },
      { code: 'N', subjectCode: 'MatN' },
      { code: 'S', subjectCode: 'MatS' },
    ],
  });

  // 11E-11F: Fre with sets
  cells.push({
    id: `y11-${week}-${day}-p1-fre-ef`,
    bandSpan: ['11E', '11F'],
    periodSpan: [1],
    subject: getSubject('Fre'),
    sets: [
      { code: 'H', subjectCode: 'FreH' },
      { code: 'O', subjectCode: 'FreO' },
    ],
  });

  // 11G-11H: PE with sets
  cells.push({
    id: `y11-${week}-${day}-p1-pe-gh`,
    bandSpan: ['11G', '11H'],
    periodSpan: [1],
    subject: getSubject('PE'),
    sets: [
      { code: 'H', subjectCode: 'PEH' },
      { code: 'S', subjectCode: 'PES' },
    ],
  });

  // Period 2: Different subjects per band group
  // 11A-11B: Sci with sets
  cells.push({
    id: `y11-${week}-${day}-p2-sci-ab`,
    bandSpan: ['11A', '11B'],
    periodSpan: [2],
    subject: getSubject('Sci'),
    sets: [
      { code: 'H', subjectCode: 'SciH' },
      { code: 'I', subjectCode: 'SciI' },
      { code: 'N', subjectCode: 'SciN' },
      { code: 'S', subjectCode: 'SciS' },
    ],
  });

  // 11C-11D: Art sets - spans period 2 and 3
  cells.push({
    id: `y11-${week}-${day}-p23-art-cd`,
    bandSpan: ['11C', '11D'],
    periodSpan: [2, 3],
    subject: getSubject('Art'),
    sets: [
      { code: 'H', subjectCode: 'ArtH' },
      { code: 'O', subjectCode: 'ArtO' },
      { code: 'L', subjectCode: 'ArtL' },
    ],
  });

  // 11E-11F: Eng with sets
  cells.push({
    id: `y11-${week}-${day}-p2-eng-ef`,
    bandSpan: ['11E', '11F'],
    periodSpan: [2],
    subject: getSubject('Eng'),
    sets: [
      { code: 'H', subjectCode: 'EngH' },
      { code: 'I', subjectCode: 'EngI' },
      { code: 'L', subjectCode: 'EngL' },
    ],
  });

  // 11G-11H: Mus sets - spans period 2 and 3
  cells.push({
    id: `y11-${week}-${day}-p23-mus-gh`,
    bandSpan: ['11G', '11H'],
    periodSpan: [2, 3],
    subject: getSubject('Mus'),
    sets: [
      { code: 'H', subjectCode: 'MusH' },
      { code: 'O', subjectCode: 'MusO' },
      { code: 'L', subjectCode: 'MusL' },
    ],
  });

  // Period 3: Cells not covered by multi-period cells
  // 11A-11B: RE
  cells.push({
    id: `y11-${week}-${day}-p3-re-ab`,
    bandSpan: ['11A', '11B'],
    periodSpan: [3],
    subject: getSubject('RE'),
  });

  // 11E-11F: DT
  cells.push({
    id: `y11-${week}-${day}-p3-dt-ef`,
    bandSpan: ['11E', '11F'],
    periodSpan: [3],
    subject: getSubject('DT'),
  });

  // Period 4: Option Block A + individual cells outside the block
  // 11A: Maths
  cells.push({
    id: `y11-${week}-${day}-p4-maths-a`,
    bandSpan: ['11A'],
    periodSpan: [4],
    subject: getSubject('Maths'),
  });

  // 11E: His
  cells.push({
    id: `y11-${week}-${day}-p4-his-e`,
    bandSpan: ['11E'],
    periodSpan: [4],
    subject: getSubject('His'),
  });

  // 11F: Geo
  cells.push({
    id: `y11-${week}-${day}-p4-geo-f`,
    bandSpan: ['11F'],
    periodSpan: [4],
    subject: getSubject('Geo'),
  });

  // Period 5: Option Block C + optional columns
  // 11A: Maths (optional)
  cells.push({
    id: `y11-${week}-${day}-p5-maths-a`,
    bandSpan: ['11A'],
    periodSpan: [5],
    subject: getSubject('Maths'),
    isOptional: true,
  });

  // 11D: Sci (optional)
  cells.push({
    id: `y11-${week}-${day}-p5-sci-d`,
    bandSpan: ['11D'],
    periodSpan: [5],
    subject: getSubject('Sci'),
    isOptional: true,
  });

  // 11E: Eng (optional)
  cells.push({
    id: `y11-${week}-${day}-p5-eng-e`,
    bandSpan: ['11E'],
    periodSpan: [5],
    subject: getSubject('Eng'),
    isOptional: true,
  });

  // Option Blocks for Period 4 (A) and Period 5 (C)
  const optionBlocks: OptionBlockPlacement[] = [
    {
      period: 4,
      blocks: [
        {
          id: 'A',
          subjects: [
            { subject: getSubject('MatI') },
            { subject: getSubject('MatC') },
            { subject: getSubject('MatN') },
            { subject: getSubject('MatS') },
            { subject: getSubject('ArtH') },
            { subject: getSubject('ArtO') },
            { subject: getSubject('ArtL') },
            { subject: getSubject('HisH') },
          ],
        },
      ],
    },
    {
      period: 5,
      blocks: [
        {
          id: 'C',
          subjects: [
            { subject: getSubject('Art') },
            { subject: getSubject('Sci') },
            { subject: getSubject('Eng') },
            { subject: getSubject('Fre') },
            { subject: getSubject('PE') },
            { subject: getSubject('Mus') },
            { subject: getSubject('DT') },
            { subject: getSubject('RE') },
            { subject: getSubject('Geo') },
            { subject: getSubject('His') },
          ],
        },
      ],
    },
  ];

  return {
    yearGroupId: 11,
    cells,
    optionBlocks,
  };
};

// ============================================================================
// YEAR 10 DATA GENERATION (Similar structure to Year 11 with diverse subjects)
// ============================================================================

const generateYear10Data = (week: Week, day: Day): YearGroupDayData => {
  const cells: TimetableCell[] = [];

  // Period 1: Science, English, PE, DT grouped by bands
  // 10A-10B: Sci sets
  cells.push({
    id: `y10-${week}-${day}-p1-sci-ab`,
    bandSpan: ['10A', '10B'],
    periodSpan: [1],
    subject: getSubject('Sci'),
    sets: [
      { code: 'H', subjectCode: 'SciH' },
      { code: 'O', subjectCode: 'SciO' },
    ],
  });

  // 10C-10D: Eng sets
  cells.push({
    id: `y10-${week}-${day}-p1-eng-cd`,
    bandSpan: ['10C', '10D'],
    periodSpan: [1],
    subject: getSubject('Eng'),
    sets: [
      { code: 'H', subjectCode: 'EngH' },
      { code: 'I', subjectCode: 'EngI' },
    ],
  });

  // 10E-10F: PE and Mus
  cells.push({
    id: `y10-${week}-${day}-p1-pe-ef`,
    bandSpan: ['10E', '10F'],
    periodSpan: [1],
    subject: getSubject('PE'),
    sets: [
      { code: 'H', subjectCode: 'PEH' },
      { code: 'O', subjectCode: 'PEO' },
    ],
  });

  // 10G-10H: DT and RE
  cells.push({
    id: `y10-${week}-${day}-p1-dt-gh`,
    bandSpan: ['10G', '10H'],
    periodSpan: [1],
    subject: getSubject('DT'),
    sets: [
      { code: 'H', subjectCode: 'DTH' },
      { code: 'O', subjectCode: 'DTO' },
    ],
  });

  // Period 2-3: French spanning two periods, Mat, and Art
  // 10A-10B: Fre - spans period 2 and 3
  cells.push({
    id: `y10-${week}-${day}-p23-fre-ab`,
    bandSpan: ['10A', '10B'],
    periodSpan: [2, 3],
    subject: getSubject('Fre'),
    sets: [
      { code: 'H', subjectCode: 'FreH' },
      { code: 'I', subjectCode: 'FreI' },
    ],
  });

  // 10C-10D: Mat for period 2, RE for period 3
  cells.push({
    id: `y10-${week}-${day}-p2-mat-cd`,
    bandSpan: ['10C', '10D'],
    periodSpan: [2],
    subject: getSubject('Mat'),
    sets: [
      { code: 'I', subjectCode: 'MatI' },
      { code: 'C', subjectCode: 'MatC' },
    ],
  });

  cells.push({
    id: `y10-${week}-${day}-p3-re-cd`,
    bandSpan: ['10C', '10D'],
    periodSpan: [3],
    subject: getSubject('RE'),
  });

  // 10E-10F: Art for period 2, His for period 3
  cells.push({
    id: `y10-${week}-${day}-p2-art-ef`,
    bandSpan: ['10E', '10F'],
    periodSpan: [2],
    subject: getSubject('Art'),
  });

  cells.push({
    id: `y10-${week}-${day}-p3-his-ef`,
    bandSpan: ['10E', '10F'],
    periodSpan: [3],
    subject: getSubject('His'),
  });

  // 10G-10H: Mus - spans period 2 and 3
  cells.push({
    id: `y10-${week}-${day}-p23-mus-gh`,
    bandSpan: ['10G', '10H'],
    periodSpan: [2, 3],
    subject: getSubject('Mus'),
    sets: [
      { code: 'H', subjectCode: 'MusH' },
      { code: 'L', subjectCode: 'MusL' },
    ],
  });

  // Period 4: Option Block B + individual cells
  cells.push({
    id: `y10-${week}-${day}-p4-eng-a`,
    bandSpan: ['10A'],
    periodSpan: [4],
    subject: getSubject('Eng'),
  });

  cells.push({
    id: `y10-${week}-${day}-p4-geo-e`,
    bandSpan: ['10E'],
    periodSpan: [4],
    subject: getSubject('Geo'),
  });

  // Period 5: Option Block C + optional columns
  cells.push({
    id: `y10-${week}-${day}-p5-pe-a`,
    bandSpan: ['10A'],
    periodSpan: [5],
    subject: getSubject('PE'),
    isOptional: true,
  });

  cells.push({
    id: `y10-${week}-${day}-p5-fre-d`,
    bandSpan: ['10D'],
    periodSpan: [5],
    subject: getSubject('Fre'),
    isOptional: true,
  });

  // Option Blocks for Period 4 (B) and Period 5 (C) with diverse subjects
  const optionBlocks: OptionBlockPlacement[] = [
    {
      period: 4,
      blocks: [
        {
          id: 'B',
          subjects: [
            { subject: getSubject('Sci') },
            { subject: getSubject('Eng') },
            { subject: getSubject('PE') },
            { subject: getSubject('DT') },
            { subject: getSubject('Fre') },
            { subject: getSubject('RE') },
            { subject: getSubject('Mus') },
            { subject: getSubject('Geo') },
            { subject: getSubject('His') },
          ],
        },
      ],
    },
    {
      period: 5,
      blocks: [
        {
          id: 'C',
          subjects: [
            { subject: getSubject('Mat') },
            { subject: getSubject('Art') },
            { subject: getSubject('Sci') },
            { subject: getSubject('Eng') },
            { subject: getSubject('PE') },
            { subject: getSubject('DT') },
            { subject: getSubject('Fre') },
            { subject: getSubject('RE') },
            { subject: getSubject('Mus') },
            { subject: getSubject('His') },
          ],
        },
      ],
    },
  ];

  return {
    yearGroupId: 10,
    cells,
    optionBlocks,
  };
};

// ============================================================================
// YEAR 9/8/7 DATA GENERATION (Based on wireframe pattern)
// Pattern: Most periods have 1 cell per band, one period has all bands grouped,
// and one period has bands in pairs with sets
// ============================================================================

// Subjects for lower years (variety of subjects)
const lowerYearSubjects = ['Art', 'Mat', 'His', 'Geo', 'Sci', 'Eng', 'PE', 'Mus', 'Fre', 'DT', 'RE'];

// Get subject code with set suffix
const getSubjectWithSet = (subjectBase: string, set: string): string => {
  return `${subjectBase}${set}`;
};

const generateLowerYearData = (yearId: YearGroupId, week: Week, day: Day): YearGroupDayData => {
  const config = yearGroupConfigs.find((c) => c.id === yearId);
  const bands = config?.bands ?? [];
  const dayIndex = allDays.indexOf(day);
  const weekOffset = week === 'B' ? 5 : 0;
  const seed = dayIndex + weekOffset + yearId * 10;

  const cells: TimetableCell[] = [];

  // Band set codes for Year 9/8/7 (H, I, L, N, O, S)
  const bandSetCodes = ['H', 'I', 'L', 'N', 'O', 'S'];

  // Determine which period has the common grouped subject (varies by day/year)
  const commonPeriod = ((seed % 3) + 2) as Period; // Period 2, 3, or 4
  // Determine which period has paired bands with sets
  const pairedPeriod = (commonPeriod === 2 ? 5 : commonPeriod === 3 ? 5 : 5) as Period;

  // Generate cells for each period
  for (let period = 1; period <= 5; period++) {
    const periodNum = period as Period;

    if (period === commonPeriod) {
      // COMMON PERIOD: All bands grouped together with one subject (like Geo in wireframe)
      const commonSubjectIndex = (seed + period) % lowerYearSubjects.length;
      const commonSubject = lowerYearSubjects[commonSubjectIndex];

      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${commonSubject}-all`,
        bandSpan: [...bands],
        periodSpan: [periodNum],
        subject: getSubject(commonSubject),
        sets: bandSetCodes.map((setCode) => ({
          code: setCode,
          subjectCode: getSubjectWithSet(commonSubject, setCode),
        })),
      });
    } else if (period === pairedPeriod) {
      // PAIRED PERIOD: Bands in pairs with 2 sets each (like Period 5 in wireframe)
      const pairSubjects = [
        lowerYearSubjects[(seed + 1) % lowerYearSubjects.length],
        lowerYearSubjects[(seed + 3) % lowerYearSubjects.length],
        lowerYearSubjects[(seed + 5) % lowerYearSubjects.length],
      ];

      // Pair 1: bands[0], bands[1]
      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[0]}-pair1`,
        bandSpan: [bands[0]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[0]),
        sets: [
          { code: bandSetCodes[0], subjectCode: getSubjectWithSet(pairSubjects[0], bandSetCodes[0]) },
          { code: bandSetCodes[1], subjectCode: getSubjectWithSet(pairSubjects[0], bandSetCodes[1]) },
        ],
      });

      // Pair 2: bands[2], bands[3]
      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[1]}-pair2`,
        bandSpan: [bands[1]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[1]),
        sets: [
          { code: bandSetCodes[2], subjectCode: getSubjectWithSet(pairSubjects[1], bandSetCodes[2]) },
          { code: bandSetCodes[3], subjectCode: getSubjectWithSet(pairSubjects[1], bandSetCodes[3]) },
        ],
      });

      // Pair 3: bands[4], bands[5]
      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[2]}-pair3`,
        bandSpan: [bands[2]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[2]),
        sets: [
          { code: bandSetCodes[0], subjectCode: getSubjectWithSet(pairSubjects[2], bandSetCodes[0]) },
          { code: bandSetCodes[1], subjectCode: getSubjectWithSet(pairSubjects[2], bandSetCodes[1]) },
        ],
      });

      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[0]}-pair4`,
        bandSpan: [bands[3]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[0]),
        sets: [
          { code: bandSetCodes[2], subjectCode: getSubjectWithSet(pairSubjects[0], bandSetCodes[2]) },
          { code: bandSetCodes[3], subjectCode: getSubjectWithSet(pairSubjects[0], bandSetCodes[3]) },
        ],
      });

      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[1]}-pair5`,
        bandSpan: [bands[4]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[1]),
        sets: [
          { code: bandSetCodes[0], subjectCode: getSubjectWithSet(pairSubjects[1], bandSetCodes[0]) },
          { code: bandSetCodes[4], subjectCode: getSubjectWithSet(pairSubjects[1], bandSetCodes[4]) },
        ],
      });

      cells.push({
        id: `y${yearId}-${week}-${day}-p${period}-${pairSubjects[2]}-pair6`,
        bandSpan: [bands[5]],
        periodSpan: [periodNum],
        subject: getSubject(pairSubjects[2]),
        sets: [
          { code: bandSetCodes[0], subjectCode: getSubjectWithSet(pairSubjects[2], bandSetCodes[0]) },
          { code: bandSetCodes[5], subjectCode: getSubjectWithSet(pairSubjects[2], bandSetCodes[5]) },
        ],
      });
    } else {
      // INDIVIDUAL PERIODS: 1 cell per 1 band (like Periods 1, 3, 4 in wireframe)
      bands.forEach((band, bandIndex) => {
        // Vary subject based on band and period for visual variety
        const subjectIndex = (seed + period + bandIndex * 2) % lowerYearSubjects.length;
        const subjectBase = lowerYearSubjects[subjectIndex];
        const setCode = bandSetCodes[bandIndex];
        const subjectCode = getSubjectWithSet(subjectBase, setCode);

        cells.push({
          id: `y${yearId}-${week}-${day}-p${period}-${band}`,
          bandSpan: [band],
          periodSpan: [periodNum],
          subject: getSubject(subjectCode),
        });
      });
    }
  }

  return {
    yearGroupId: yearId,
    cells,
  };
};

// ============================================================================
// MAIN DATA GENERATOR
// ============================================================================

const generateDayData = (): DayData[] => {
  const result: DayData[] = [];

  allWeeks.forEach((week) => {
    allDays.forEach((day) => {
      const yearGroups: YearGroupDayData[] = [
        generateYear11Data(week, day),
        generateYear10Data(week, day),
        generateLowerYearData(9, week, day),
        generateLowerYearData(8, week, day),
        generateLowerYearData(7, week, day),
      ];

      result.push({
        week,
        day,
        yearGroups,
      });
    });
  });

  return result;
};

// ============================================================================
// EXPORTED DATA
// ============================================================================

export const classV2MockData: ClassV2Data = {
  yearGroupConfigs,
  weeks: allWeeks,
  days: allDays,
  periodsPerDay,
  dayData: generateDayData(),
};

export default classV2MockData;
